import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Target, Zap, Users, Heart } from 'lucide-react';

const About = () => {
  const { t } = useTranslation();
  
  const stats = t('about.stats', { returnObjects: true }) as Array<{
    value: string;
    label: string;
  }>;

  return (
    <section id="about" className="relative py-24 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-crypto-dark">
        <div className="absolute top-0 right-0 w-[700px] h-[700px] bg-crypto-red/5 rounded-full blur-[200px]" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-crypto-purple/5 rounded-full blur-[150px]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block px-4 py-2 rounded-full bg-crypto-red/10 border border-crypto-red/30 text-crypto-red text-sm font-medium mb-6">
              {t('about.subtitle')}
            </span>
            
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              {t('about.title')}
            </h2>
            
            <div className="prose prose-invert max-w-none">
              <p className="text-gray-400 text-lg leading-relaxed whitespace-pre-line">
                {t('about.description')}
              </p>
            </div>

            {/* Features list */}
            <div className="mt-8 grid grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-crypto-red/10 rounded-lg flex items-center justify-center">
                  <Target className="w-5 h-5 text-crypto-red" />
                </div>
                <span className="text-gray-300 text-sm">Практикующие эксперты</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-crypto-red/10 rounded-lg flex items-center justify-center">
                  <Zap className="w-5 h-5 text-crypto-red" />
                </div>
                <span className="text-gray-300 text-sm">Актуальные материалы</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-crypto-red/10 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-crypto-red" />
                </div>
                <span className="text-gray-300 text-sm">Живая поддержка</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-crypto-red/10 rounded-lg flex items-center justify-center">
                  <Heart className="w-5 h-5 text-crypto-red" />
                </div>
                <span className="text-gray-300 text-sm">Реальные результаты</span>
              </div>
            </div>
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="grid grid-cols-2 gap-6"
          >
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.05 }}
                className="relative group"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-crypto-red/20 to-crypto-accent/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="relative bg-crypto-card border border-crypto-red/10 rounded-2xl p-8 text-center hover:border-crypto-red/30 transition-all duration-300">
                  <div className="text-4xl md:text-5xl font-bold text-crypto-red mb-2">
                    {stat.value}
                  </div>
                  <div className="text-gray-400">{stat.label}</div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;
